/**
 * Enhanced validation utilities for quiz questions
 */

/**
 * Safely convert value to string
 * @param {any} value - Value to convert
 * @returns {string} - String representation of the value
 */
export const safeToString = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  return String(value);
};

/**
 * Safely convert value to number
 * @param {any} value - Value to convert
 * @param {number} defaultValue - Default value if conversion fails
 * @returns {number} - Numeric representation of the value
 */
export const safeToNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

/**
 * Safely convert value to integer
 * @param {any} value - Value to convert
 * @param {number} defaultValue - Default value if conversion fails
 * @returns {number} - Integer representation of the value
 */
export const safeToInteger = (value, defaultValue = 0) => {
  const num = safeToNumber(value, defaultValue);
  return Math.floor(num);
};

/**
 * Validate and sanitize question data types
 * @param {Object} question - Question object to sanitize
 * @returns {Object} - Sanitized question object
 */
export const sanitizeQuestionData = (question) => {
  const sanitized = { ...question };

  // Ensure all string fields are properly converted
  sanitized.questionText = safeToString(question.questionText).trim();
  sanitized.questionClue = safeToString(question.questionClue).trim();
  sanitized.questionType = safeToString(question.questionType).trim();
  sanitized.answerType = safeToString(question.answerType).trim();

  // Ensure numeric fields are properly converted
  sanitized.timeLimit = safeToInteger(question.timeLimit, 30);
  sanitized.rowNumber = safeToInteger(question.rowNumber, 1);

  // Ensure ID fields are properly converted
  sanitized.questionBank = safeToInteger(question.questionBank);
  sanitized.difficulty = safeToInteger(question.difficulty);
  sanitized.questionMood = safeToInteger(question.questionMood);
  sanitized.questionTopic = safeToInteger(question.questionTopic);
  sanitized.songId = safeToInteger(question.songId);

  // Sanitize answers array
  if (Array.isArray(question.answers)) {
    sanitized.answers = question.answers.map(answer => ({
      text: safeToString(answer.text).trim(),
      correct: Boolean(answer.correct),
      reason: safeToString(answer.reason || '').trim()
    }));
  } else {
    sanitized.answers = [];
  }

  return sanitized;
};

/**
 * Validate question text
 * @param {string} questionText - The question text to validate
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateQuestionText = (questionText) => {
  const errors = [];
  
  if (!questionText || typeof questionText !== 'string') {
    errors.push('Question text is required');
    return { isValid: false, errors };
  }
  
  const trimmedText = questionText.trim();
  
  if (trimmedText.length < 10) {
    errors.push('Question text must be at least 10 characters long');
  }
  
  if (trimmedText.length > 1000) {
    errors.push('Question text must be less than 1000 characters');
  }
  
  // Check for basic question structure
  const hasQuestionMark = trimmedText.includes('?');
  const endsWithQuestionMark = trimmedText.endsWith('?');
  
  if (!hasQuestionMark && !endsWithQuestionMark) {
    // This is a warning, not an error
    // errors.push('Question should typically end with a question mark');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate time limit
 * @param {number} timeLimit - Time limit in seconds
 * @returns {Object} - Validation result
 */
export const validateTimeLimit = (timeLimit) => {
  const errors = [];
  
  if (timeLimit === null || timeLimit === undefined) {
    errors.push('Time limit is required');
    return { isValid: false, errors };
  }
  
  const numericTimeLimit = Number(timeLimit);
  
  if (isNaN(numericTimeLimit)) {
    errors.push('Time limit must be a number');
  } else {
    if (numericTimeLimit < 5) {
      errors.push('Time limit must be at least 5 seconds');
    }
    
    if (numericTimeLimit > 300) {
      errors.push('Time limit must be less than 300 seconds (5 minutes)');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate answers array
 * @param {Array} answers - Array of answer objects
 * @param {string} answerType - Type of answer (single_choice, multiple_choice, true_false)
 * @returns {Object} - Validation result
 */
export const validateAnswers = (answers, answerType) => {
  const errors = [];
  
  if (!answers || !Array.isArray(answers)) {
    errors.push('Answers array is required');
    return { isValid: false, errors };
  }
  
  if (answers.length < 2) {
    errors.push('At least 2 answers are required');
  }
  
  if (answers.length > 4) {
    errors.push('Maximum 4 answers are allowed');
  }
  
  // Validate individual answers
  const validAnswers = answers.filter(answer => 
    answer && 
    typeof answer.text === 'string' && 
    answer.text.trim().length > 0
  );
  
  if (validAnswers.length !== answers.length) {
    errors.push('All answers must have valid text');
  }
  
  // Check for duplicate answer texts
  const answerTexts = validAnswers.map(a => a.text.trim().toLowerCase());
  const uniqueTexts = new Set(answerTexts);
  if (answerTexts.length !== uniqueTexts.size) {
    errors.push('Answer texts must be unique');
  }
  
  // Validate answer text length
  validAnswers.forEach((answer, index) => {
    if (answer.text.trim().length > 200) {
      errors.push(`Answer ${index + 1} text is too long (max 200 characters)`);
    }
  });
  
  // Count correct answers
  const correctAnswers = validAnswers.filter(answer => answer.correct === true);
  
  if (correctAnswers.length === 0) {
    errors.push('At least one answer must be marked as correct');
  }
  
  // Answer type specific validation
  switch (answerType) {
    case 'true_false':
      if (validAnswers.length !== 2) {
        errors.push('True/False questions must have exactly 2 answers');
      }
      if (correctAnswers.length !== 1) {
        errors.push('True/False questions must have exactly 1 correct answer');
      }
      break;
      
    case 'single_choice':
      if (correctAnswers.length !== 1) {
        errors.push('Single choice questions must have exactly 1 correct answer');
      }
      break;
      
    case 'multiple_choice':
      if (correctAnswers.length < 1) {
        errors.push('Multiple choice questions must have at least 1 correct answer');
      }
      if (correctAnswers.length >= validAnswers.length) {
        errors.push('Multiple choice questions cannot have all answers correct');
      }
      break;
      
    default:
      // No specific validation for unknown answer types
      break;
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate question clue
 * @param {string} questionClue - Optional question clue
 * @returns {Object} - Validation result
 */
export const validateQuestionClue = (questionClue) => {
  const errors = [];
  
  if (questionClue && typeof questionClue === 'string') {
    if (questionClue.trim().length > 500) {
      errors.push('Question clue must be less than 500 characters');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate required question metadata
 * @param {Object} question - Question object
 * @param {Object} lookupData - Available lookup data
 * @returns {Object} - Validation result
 */
export const validateQuestionMetadata = (question, lookupData = {}) => {
  const errors = [];
  
  // Validate question bank
  if (!question.questionBank) {
    errors.push('Question bank is required');
  } else if (lookupData.questionBanks) {
    const validBank = lookupData.questionBanks.find(bank => 
      bank.bank_id === question.questionBank
    );
    if (!validBank) {
      errors.push('Invalid question bank selected');
    }
  }
  
  // Validate song
  if (!question.songId) {
    errors.push('Song selection is required');
  }
  
  // Validate question type
  if (!question.questionType) {
    errors.push('Question type is required');
  } else if (lookupData.questionTypes) {
    const validType = lookupData.questionTypes.find(type => 
      type.type_name === question.questionType
    );
    if (!validType) {
      errors.push('Invalid question type selected');
    }
  }
  
  // Validate answer type
  if (!question.answerType) {
    errors.push('Answer type is required');
  } else if (lookupData.answerTypes) {
    const validType = lookupData.answerTypes.find(type => 
      type.type_name === question.answerType
    );
    if (!validType) {
      errors.push('Invalid answer type selected');
    }
  }
  
  // Validate difficulty
  if (!question.difficulty) {
    errors.push('Difficulty level is required');
  } else if (lookupData.difficulties) {
    const validDifficulty = lookupData.difficulties.find(diff => 
      diff.difficulty_id === question.difficulty
    );
    if (!validDifficulty) {
      errors.push('Invalid difficulty level selected');
    }
  }
  
  // Validate mood
  if (!question.questionMood) {
    errors.push('Question mood is required');
  } else if (lookupData.questionMoods) {
    const validMood = lookupData.questionMoods.find(mood => 
      mood.mood_type_id === question.questionMood
    );
    if (!validMood) {
      errors.push('Invalid question mood selected');
    }
  }
  
  // Validate topic
  if (!question.questionTopic) {
    errors.push('Question topic is required');
  } else if (lookupData.questionTopics) {
    const validTopic = lookupData.questionTopics.find(topic => 
      topic.topic_type_id === question.questionTopic
    );
    if (!validTopic) {
      errors.push('Invalid question topic selected');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validate data types and structure
 * @param {Object} question - Question object to validate
 * @returns {Object} - Data type validation result
 */
export const validateQuestionDataTypes = (question) => {
  const errors = [];

  try {
    // Validate basic structure
    if (!question || typeof question !== 'object') {
      errors.push('Question must be a valid object');
      return { isValid: false, errors };
    }

    // Validate required fields exist
    const requiredFields = ['questionText', 'timeLimit', 'answers', 'questionType', 'answerType', 'difficulty', 'questionBank', 'songId'];
    requiredFields.forEach(field => {
      if (!(field in question)) {
        errors.push(`Missing required field: ${field}`);
      }
    });

    // Validate data types
    if (question.questionText !== undefined && typeof question.questionText !== 'string') {
      errors.push('Question text must be a string');
    }

    if (question.timeLimit !== undefined && (typeof question.timeLimit !== 'number' || isNaN(question.timeLimit))) {
      errors.push('Time limit must be a valid number');
    }

    if (question.answers !== undefined && !Array.isArray(question.answers)) {
      errors.push('Answers must be an array');
    }

    // Validate ID fields are numbers
    const idFields = ['questionBank', 'difficulty', 'questionMood', 'questionTopic', 'songId', 'rowNumber'];
    idFields.forEach(field => {
      if (question[field] !== undefined && question[field] !== null && question[field] !== '') {
        const value = Number(question[field]);
        if (isNaN(value)) {
          errors.push(`${field} must be a valid number (received: ${typeof question[field]} - ${question[field]})`);
        }
      }
    });

    // Validate answers structure
    if (Array.isArray(question.answers)) {
      question.answers.forEach((answer, index) => {
        if (!answer || typeof answer !== 'object') {
          errors.push(`Answer ${index + 1} must be a valid object`);
        } else {
          if (typeof answer.text !== 'string') {
            errors.push(`Answer ${index + 1} text must be a string`);
          }
          if (typeof answer.correct !== 'boolean') {
            errors.push(`Answer ${index + 1} correct flag must be a boolean`);
          }
        }
      });
    }

  } catch (error) {
    errors.push(`Data type validation error: ${error.message}`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Comprehensive question validation with data sanitization
 * @param {Object} question - Question object to validate
 * @param {Object} lookupData - Available lookup data for validation
 * @returns {Object} - Complete validation result
 */
export const validateQuestion = (question, lookupData = {}) => {
  const allErrors = [];

  // First, sanitize the question data
  let sanitizedQuestion;
  try {
    sanitizedQuestion = sanitizeQuestionData(question);
  } catch (error) {
    allErrors.push(`Data sanitization error: ${error.message}`);
    return {
      isValid: false,
      errors: allErrors,
      sanitizedQuestion: question
    };
  }

  // Validate data types
  const dataTypeValidation = validateQuestionDataTypes(sanitizedQuestion);
  allErrors.push(...dataTypeValidation.errors);

  // If data type validation fails, don't proceed with other validations
  if (!dataTypeValidation.isValid) {
    return {
      isValid: false,
      errors: allErrors,
      sanitizedQuestion,
      details: {
        dataTypes: dataTypeValidation
      }
    };
  }

  // Validate question text
  const textValidation = validateQuestionText(sanitizedQuestion.questionText);
  allErrors.push(...textValidation.errors);

  // Validate time limit
  const timeValidation = validateTimeLimit(sanitizedQuestion.timeLimit);
  allErrors.push(...timeValidation.errors);

  // Validate answers
  const answersValidation = validateAnswers(sanitizedQuestion.answers, sanitizedQuestion.answerType);
  allErrors.push(...answersValidation.errors);

  // Validate question clue
  const clueValidation = validateQuestionClue(sanitizedQuestion.questionClue);
  allErrors.push(...clueValidation.errors);

  // Validate metadata
  const metadataValidation = validateQuestionMetadata(sanitizedQuestion, lookupData);
  allErrors.push(...metadataValidation.errors);

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
    sanitizedQuestion,
    details: {
      dataTypes: dataTypeValidation,
      text: textValidation,
      time: timeValidation,
      answers: answersValidation,
      clue: clueValidation,
      metadata: metadataValidation
    }
  };
};

/**
 * Validate multiple questions
 * @param {Array} questions - Array of questions to validate
 * @param {Object} lookupData - Available lookup data
 * @returns {Array} - Array of validation results
 */
export const validateQuestions = (questions, lookupData = {}) => {
  if (!questions || !Array.isArray(questions)) {
    return [];
  }
  
  return questions.map(question => validateQuestion(question, lookupData));
};

/**
 * Get validation summary statistics
 * @param {Array} validationResults - Array of validation results
 * @returns {Object} - Summary statistics
 */
export const getValidationSummary = (validationResults) => {
  const total = validationResults.length;
  const valid = validationResults.filter(result => result.isValid).length;
  const invalid = total - valid;
  
  const errorCounts = {};
  validationResults.forEach(result => {
    result.errors.forEach(error => {
      errorCounts[error] = (errorCounts[error] || 0) + 1;
    });
  });
  
  const mostCommonErrors = Object.entries(errorCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5)
    .map(([error, count]) => ({ error, count }));
  
  return {
    total,
    valid,
    invalid,
    validPercentage: total > 0 ? Math.round((valid / total) * 100) : 0,
    errorCounts,
    mostCommonErrors
  };
};
